import type { AnyListConfig, ListOrderingOption, ListPresentationDirection, ListPresentationValue } from "@/types/list";
import type { Row, SortingFn, SortingState } from "@tanstack/react-table";
import { getProperty } from "dot-prop";

const EXPORT_PROP_EXPRESSION_PATTERN = /^prop\(row, (".*")\)$/u;

/**
 * Type-aware comparison for sorting. Handles Date, number, string, and null values.
 * Nulls always sort last regardless of direction.
 */
export function comparePresentationValues(
  leftValue: ListPresentationValue,
  rightValue: ListPresentationValue,
): number {
  if (leftValue == null && rightValue == null) return 0;
  if (leftValue == null) return 1;
  if (rightValue == null) return -1;

  if (leftValue instanceof Date && rightValue instanceof Date) {
    return leftValue.getTime() - rightValue.getTime();
  }

  if (typeof leftValue === "number" && typeof rightValue === "number") {
    return leftValue - rightValue;
  }

  return String(leftValue).localeCompare(String(rightValue), undefined, {
    numeric: true,
    sensitivity: "base",
  });
}

/**
 * Creates a TanStack sortingFn from an ordering option's getValue function.
 */
export function createSortingFn<$Row>(
  getValue: (row: $Row) => ListPresentationValue,
): SortingFn<$Row> {
  return (rowA: Row<$Row>, rowB: Row<$Row>) => comparePresentationValues(getValue(rowA.original), getValue(rowB.original));
}

function toPresentationValue(value: unknown): ListPresentationValue {
  if (
    value == null
    || typeof value === "string"
    || typeof value === "number"
    || typeof value === "boolean"
    || value instanceof Date
  ) {
    return value;
  }

  return null;
}

function getExportPropertyPath(celExpression: string | undefined): string | null {
  if (!celExpression) {
    return null;
  }

  const match = EXPORT_PROP_EXPRESSION_PATTERN.exec(celExpression);
  if (!match?.[1]) {
    return null;
  }

  try {
    const propertyPath = JSON.parse(match[1]);

    if (typeof propertyPath !== "string" || propertyPath.startsWith("'") || propertyPath.includes("[")) {
      return null;
    }

    return propertyPath;
  } catch {
    return null;
  }
}

function getRowPropertyValue(row: unknown, propertyPath: string): ListPresentationValue {
  if (typeof row !== "object" || row === null) {
    return null;
  }

  return toPresentationValue(getProperty(row, propertyPath));
}

function buildNestedOrderBy(propertyPath: string, direction: ListPresentationDirection): Record<string, unknown> {
  return propertyPath
    .split(".")
    .reverse()
    .reduce<Record<string, unknown> | ListPresentationDirection>(
      (orderBy, pathSegment) => ({ [pathSegment]: orderBy }),
      direction,
    ) as Record<string, unknown>;
}

function isInferredSortablePropertyPath(listConfig: AnyListConfig, propertyPath: string) {
  const firstSegment = propertyPath.split(".")[0];

  return firstSegment !== undefined
    && !(firstSegment in listConfig.computedSelect)
    && !propertyPath.split(".").some((segment) => segment.startsWith("_") || /^\d+$/u.test(segment));
}

/**
 * Resolves the TanStack column ID for an ordering option.
 * Uses explicit `columnId` if set, otherwise falls back to `label` (which matches column header keys).
 */
export function getColumnIdForOrdering<$Row>(option: ListOrderingOption<$Row>): string {
  return option.columnId ?? option.label;
}

export function getListOrderingOptions(listConfig: AnyListConfig): ListOrderingOption[] {
  const explicitOptions = listConfig.presentation?.orderingOptions as ListOrderingOption[] | undefined;
  const orderingOptions = (explicitOptions ?? []).map((option) => {
    if (option.orderBy) {
      return option;
    }

    const columnId = getColumnIdForOrdering(option);
    const columnConfig = listConfig.columns[columnId] ?? listConfig.columns[option.key];
    const propertyPath = getExportPropertyPath(columnConfig?.export?.cel);

    if (!propertyPath || !isInferredSortablePropertyPath(listConfig, propertyPath)) {
      return option;
    }

    return {
      ...option,
      orderBy: (direction) => [buildNestedOrderBy(propertyPath, direction)],
    } satisfies ListOrderingOption;
  });
  const explicitlyMappedColumnIds = new Set(orderingOptions.map((option) => getColumnIdForOrdering(option)));
  const explicitlyMappedKeys = new Set(orderingOptions.map((option) => option.key));

  const inferredOptions = Object.entries(listConfig.columns).flatMap(([columnId, columnConfig]) => {
    if (columnConfig.display?.sortable === false || explicitlyMappedColumnIds.has(columnId) || explicitlyMappedKeys.has(columnId)) {
      return [];
    }

    const propertyPath = getExportPropertyPath(columnConfig.export?.cel);
    if (!propertyPath || !isInferredSortablePropertyPath(listConfig, propertyPath)) {
      return [];
    }

    return [
      {
        key: columnId,
        label: columnId,
        columnId,
        getValue: (row: unknown) => getRowPropertyValue(row, propertyPath),
        orderBy: (direction) => [buildNestedOrderBy(propertyPath, direction)],
      } satisfies ListOrderingOption,
    ];
  });

  return [...orderingOptions, ...inferredOptions];
}

export function isServerListOrderingOption<$Row>(option: ListOrderingOption<$Row>) {
  return option.serverSort === true || option.orderBy !== undefined;
}

export function getServerListOrderingOptions(listConfig: AnyListConfig): ListOrderingOption[] {
  return getListOrderingOptions(listConfig).filter(isServerListOrderingOption);
}

/**
 * Converts orderBy/orderDirection display state into TanStack SortingState.
 */
export function orderingToSortingState<$Row>(
  orderBy: string | null | undefined,
  orderDirection: ListPresentationDirection | undefined,
  orderingOptions: Array<ListOrderingOption<$Row>>,
): SortingState {
  if (!orderBy) return [];

  const option = orderingOptions.find((o) => o.key === orderBy);
  if (!option) return [];

  return [{ id: getColumnIdForOrdering(option), desc: (orderDirection ?? "asc") === "desc" }];
}

/**
 * Builds a map from column ID → ordering option for quick lookup.
 */
export function buildColumnToOrderingMap<$Row>(
  orderingOptions: Array<ListOrderingOption<$Row>>,
): Map<string, ListOrderingOption<$Row>> {
  const map = new Map<string, ListOrderingOption<$Row>>();
  for (const option of orderingOptions) {
    map.set(getColumnIdForOrdering(option), option);
  }
  return map;
}
